// src/models/buildingModel.js - Frontend Building Model
import api from '../services/api';




// Building Management Functions

// Get all buildings with filters
export const getAllBuildings = async (filters = {}) => {
    try {
        const response = await api.get('/buildings', { params: filters });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching buildings' };
    }
};

// Get building by ID
export const getBuilding = async (id) => {
    try {
        const response = await api.get(`/buildings/${id}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching building' };
    }
};

// Create new building
export const createBuilding = async (buildingData) => {
    try {
        const response = await api.post('/buildings', buildingData);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error creating building' };
    }
};

// Update building
export const updateBuilding = async (id, buildingData) => {
    try {
        const response = await api.put(`/buildings/${id}`, buildingData);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error updating building' };
    }
};

// Delete building
export const deleteBuilding = async (id) => {
    try {
        const response = await api.delete(`/buildings/${id}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error deleting building' };
    }
};

// Get building apartments
export const getBuildingApartments = async (id) => {
    try {
        const response = await api.get(`/buildings/${id}/properties`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching building apartments' };
    }
};

// Get building floor plan
export const getBuildingFloorPlan = async (id) => {
    try {
        const response = await api.get(`/buildings/${id}/floor-plan`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching building floor plan' };
    }
};

// Create bulk apartments for building
// Updated bulkCreateProperties function in buildingModel.js
export const bulkCreateProperties = async (buildingId, apartments) => {
    try {
        // Make sure apartments is an array
        if (!Array.isArray(apartments)) {
            console.error('Apartments is not an array:', apartments);
            throw new Error('Apartments must be an array');
        }

        // Log the request payload
        console.log('API Request:', `${API_URL}/buildings/${buildingId}/bulk-properties`);
        console.log('Request Data:', { apartments });

        const response = await api.post(
            `/buildings/${buildingId}/bulk-properties`,
            { apartments }
        );

        // Log the response
        console.log('API Response:', response.data);

        return response.data;
    } catch (error) {
        console.error('Bulk create properties error details:', error.response || error);
        console.error('Request was:', { buildingId, apartments });

        // Provide more detailed error information
        if (error.response) {
            console.error('Error response data:', error.response.data);
            console.error('Error response status:', error.response.status);
        }

        throw error.response?.data || {
            success: false,
            message: error.message || 'Error creating bulk apartments'
        };
    }
};

// Create floor plan for building
export const createFloorPlan = async (buildingId, floorPlanData) => {
    try {
        const response = await api.post(`/buildings/${buildingId}/floor-plans`, floorPlanData);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error creating floor plan' };
    }
};

// Helper Functions

// Format building display name
export const formatBuildingDisplayName = (building) => {
    return `${building.name} - ${building.address}`;
};

// Calculate occupancy rate
export const calculateOccupancyRate = (building) => {
    if (!building.totalApartments || building.totalApartments === 0) {
        return 0;
    }
    return ((building.occupied_apartments || 0) / building.totalApartments * 100).toFixed(1);
};

// Format currency
export const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }).format(amount || 0);
};

// Validate building data
export const validateBuildingData = (buildingData) => {
    const errors = {};

    if (!buildingData.name?.trim()) {
        errors.name = 'Building name is required';
    }

    if (!buildingData.address?.trim()) {
        errors.address = 'Building address is required';
    }

    if (!buildingData.totalFloors || buildingData.totalFloors < 1) {
        errors.totalFloors = 'Total floors must be at least 1';
    }

    if (buildingData.totalApartments === undefined || buildingData.totalApartments < 0) {
        errors.totalApartments = 'Total apartments must be at least 0';
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

export default {
    getAllBuildings,
    getBuilding,
    createBuilding,
    updateBuilding,
    deleteBuilding,
    getBuildingApartments,
    getBuildingFloorPlan,
    bulkCreateProperties,
    createFloorPlan,
    formatBuildingDisplayName,
    calculateOccupancyRate,
    formatCurrency,
    validateBuildingData
};